import { useHistory } from 'react-router-dom';

import { selectUIState } from 'store/UI/selectors';
import { useAppSelector } from 'store/hooks';

/**
 * Hook to handle back navigation with history fallback to parent route
 *
 * @param parentPath - The fallback route to navigate to if there's no browser history
 * @returns A function that navigates back in history or to the parent path
 *
 * @example
 * const handleBack = useBackNavigation('/parent/route');
 * <Button onClick={handleBack}>Back</Button>
 */
export function useBackNavigation(parentPath: string): () => void {
  const history = useHistory();
  const { prevPathname } = useAppSelector(selectUIState);

  return () => {
    // If prevPathname is null, we're on a reload/initial load, so use parentPath
    if (!prevPathname || prevPathname === history.location.pathname) {
      history.push(parentPath);
      return;
    }

    // Otherwise, we've navigated within the app, so go back
    history.goBack();
  };
}

export default useBackNavigation;
